import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './chrome/AppShell';
import { getRoutes } from './modules';
import { RequireAuth } from './modules/auth';
import { InstallOnboarding } from './onboarding/InstallOnboarding';
import { SettingsPage } from './settings/SettingsPage';

/**
 * Module routes reachable while logged out. Everything else is wrapped in `RequireAuth`
 * (Phase 0 integration, WS-2 ↔ WS-3): household content needs a session, and the sync
 * socket would be refused anyway (`authenticateUpgrade`, server `src/sync/index.ts`).
 *
 * These render their own full-page layout, so they sit *outside* the app chrome.
 *
 * `/play/:token` (WS-23) is the third, and the only one a person with no account is meant to
 * reach and *stay* on: it holds a guest play link, and the household nav would be both
 * meaningless and misleading to somebody who can only ever open one game.
 */
const PUBLIC_MODULE_ROUTES = new Set([
  '/login',
  '/invite/:token',
  '/play/:token',
  // Friend of the family (guest lounge + a friend's matches) — guest-session pages, same posture.
  '/play/lounge',
  '/play/g/:gameId',
]);

function protect(Component: ComponentType): JSX.Element {
  return (
    <RequireAuth>
      <Component />
    </RequireAuth>
  );
}

/**
 * `/` used to render the WS-0 scaffold's hello/health page (Dan: "right now it's just a
 * confirmation the server is working"). The digest module's day-at-a-glance view is the
 * designed "home" (ROADMAP §2), so root just redirects there. `/digest` keeps working on
 * its own path too — push notifications deep-link straight to it.
 */
function IndexRedirect(): JSX.Element {
  return <Navigate to="/digest" replace />;
}

/**
 * Router (WS-3 chose react-router). The registry stays router-agnostic (`ModuleRoute` is
 * a plain `{ path, component }`, see docs/ARCHITECTURE.md) — this is the one place that
 * maps it onto react-router's data-router API, alongside the shell's own routes.
 */
export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  const moduleRoutes = getRoutes();

  return createBrowserRouter([
    ...moduleRoutes
      .filter((route) => PUBLIC_MODULE_ROUTES.has(route.path))
      .map((route) => ({ path: route.path, element: <route.component /> })),
    {
      path: '/',
      element: <AppShell />,
      children: [
        // Public on purpose: iOS members must be able to read the install instructions
        // (push only works from a home-screen install) before they have an account.
        { path: 'install', element: <InstallOnboarding /> },
        { index: true, element: protect(IndexRedirect) },
        { path: 'settings', element: protect(SettingsPage) },
        ...moduleRoutes
          .filter((route) => !PUBLIC_MODULE_ROUTES.has(route.path))
          .map((route) => ({
            // react-router children paths are relative to the parent ('/'); module routes
            // are authored as absolute paths (e.g. '/lists/:listId') per the registry contract.
            path: route.path.replace(/^\//, ''),
            element: protect(route.component),
          })),
      ],
    },
  ]);
}
